import React, { useEffect, useState } from "react";
import { FinetuningService } from "../../services/FinetuningService";
import { DatasetService } from "../../services/DatasetService";
import { usePermissions } from "../../PermissionsProvider";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import CustomSwitch from "./switch/CustomSwitch";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { Tooltip } from "../ui/tooltip";
import { Separator } from "../ui/separator";
import { 
  InfoIcon, 
  PlusIcon, 
  XIcon,
  Loader2Icon,
  SparklesIcon
} from "lucide-react";

interface FinetuneSettingsProps {
  exportPrompt: string;
  setExportPrompt: (value: string) => void;
  fourbit: boolean;
  setFourbit: (value: boolean) => void;
  isExporting: boolean;
  baseModel: string;
  setBaseModel: (value: string) => void;
  promptDeletions: string[];
  setPromptDeletions: (value: string[]) => void | null;
  warmupSteps: number;
  setWarmupSteps: (value: number) => void;
  warmupRatio: number;
  setWarmupRatio: (value: number) => void;
  learningRate: number;
  setLearningRate: (value: number) => void;
  perDeviceTrainBatchSize?: number;
  setPerDeviceTrainBatchSize?: (value: number) => void;
  gradientAccumulationSteps?: number;
  setGradientAccumulationSteps?: (value: number) => void;
  trainOnResponsesOnly?: boolean;
  setTrainOnResponsesOnly?: (value: boolean) => void;
  trainSplit?: number;
  setTrainSplit?: (value: number) => void;
  alpha?: number;
  setAlpha?: (value: number) => void;
  rank?: number;
  setRank?: (value: number) => void;
  epochs?: number;
  setEpochs?: (value: number) => void;
  weightDecay?: number;
  setWeightDecay?: (value: number) => void;
  dropout?: number;
  setDropout?: (value: number) => void;
  rslora?: boolean;
  setRslora?: (value: boolean) => void;
  hideAdvancedSettings?: boolean;
  showOnlyAdvancedSettings?: boolean;
  intentGroupId?: number | null;
  selectedSubDataset?: FinetuneDataset | null;
  setSelectedSubDataset?: (dataset: FinetuneDataset | null) => void;
  displayName?: string;
  setDisplayName?: (value: string) => void;
  datasetId: number;
}

// Helper component for field label (works for both required and optional fields)
const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  tooltip?: string;
}> = ({ label, required = false, tooltip }) => (
  <div className="flex items-center gap-2 mb-2">
    <Label className="text-sm font-gc-med text-foreground">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </Label>
    {tooltip && (
      <Tooltip content={tooltip}>
        <InfoIcon className="w-4 h-4 text-muted-foreground cursor-help" />
      </Tooltip>
    )}
  </div>
);

const FinetuneSettings: React.FC<FinetuneSettingsProps> = ({
  exportPrompt,
  setExportPrompt,
  fourbit,
  setFourbit,
  isExporting,
  baseModel,
  setBaseModel,
  promptDeletions,
  setPromptDeletions,
  warmupSteps,
  setWarmupSteps,
  warmupRatio,
  setWarmupRatio,
  learningRate,
  setLearningRate,
  perDeviceTrainBatchSize,
  setPerDeviceTrainBatchSize,
  gradientAccumulationSteps,
  setGradientAccumulationSteps,
  trainOnResponsesOnly,
  setTrainOnResponsesOnly,
  trainSplit,
  setTrainSplit,
  alpha,
  setAlpha,
  rank,
  setRank,
  epochs,
  setEpochs,
  weightDecay,
  setWeightDecay,
  dropout,
  setDropout,
  rslora,
  setRslora,
  hideAdvancedSettings = false,
  showOnlyAdvancedSettings = false,
  intentGroupId = null,
  selectedSubDataset = null,
  setSelectedSubDataset,
  datasetId,
}) => {
  const { apiKey } = usePermissions();
  const [baseModels, setBaseModels] = useState<string[]>([]);
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState<boolean>(false);
  const [promptLoading, setPromptLoading] = useState<boolean>(false);

  // Input field raw text values
  const [warmupRatioText, setWarmupRatioText] = useState<string>(
    warmupRatio?.toString() || ""
  );
  const [learningRateText, setLearningRateText] = useState<string>(
    learningRate?.toString() || ""
  );
  const [warmupStepsText, setWarmupStepsText] = useState<string>(
    warmupSteps?.toString() || ""
  );
  const [perDeviceTrainBatchSizeText, setPerDeviceTrainBatchSizeText] =
    useState<string>(perDeviceTrainBatchSize?.toString() || "");
  const [gradientAccumulationStepsText, setGradientAccumulationStepsText] =
    useState<string>(gradientAccumulationSteps?.toString() || "");
  const [alphaText, setAlphaText] = useState<string>(alpha?.toString() || "");
  const [rankText, setRankText] = useState<string>(rank?.toString() || "");
  const [epochsText, setEpochsText] = useState<string>(
    epochs?.toString() || ""
  );
  const [weightDecayText, setWeightDecayText] = useState<string>(
    (weightDecay ?? 0).toString()
  );
  const [dropoutText, setDropoutText] = useState<string>(
    dropout !== undefined && dropout !== null ? dropout.toString() : "0.05"
  );

  // Update text states when props change
  useEffect(() => {
    setWarmupRatioText(warmupRatio?.toString() || "");
    setLearningRateText(learningRate?.toString() || "");
    setWarmupStepsText(warmupSteps?.toString() || "");
    setPerDeviceTrainBatchSizeText(perDeviceTrainBatchSize?.toString() || "");
    setGradientAccumulationStepsText(
      gradientAccumulationSteps?.toString() || ""
    );
    setAlphaText(alpha?.toString() || "");
    setRankText(rank?.toString() || "");
    setEpochsText(epochs?.toString() || "");
    setWeightDecayText((weightDecay ?? 0).toString());
    setDropoutText(
      dropout !== undefined && dropout !== null ? dropout.toString() : "0.05"
    );
  }, [
    warmupRatio,
    learningRate,
    warmupSteps,
    perDeviceTrainBatchSize,
    gradientAccumulationSteps,
    alpha,
    rank,
    epochs,
    weightDecay,
    dropout,
    rslora,
  ]);

  const getPrompt = () => {
    let selectedDatasetId;
    if (selectedSubDataset) {
      selectedDatasetId = selectedSubDataset.id;
    } else {
      selectedDatasetId = datasetId;
    }

    DatasetService.getDatasetPrompt(
      selectedDatasetId,
      setPromptLoading,
      apiKey
    ).subscribe(
      (prompt: { prompt: string }) => {
        setExportPrompt(prompt.prompt);
      },
      (error: any) => {
        console.error("Failed to fetch prompt:", error);
      }
    );
  };

  // Fetch base models
  useEffect(() => {
    if (!showOnlyAdvancedSettings) {
      FinetuningService.getBaseModels(() => {}, apiKey).subscribe(
        (models: string[]) => {
          setBaseModels(models);
          if (models.length > 0 && !baseModel) {
            setBaseModel(models[0]);
          }
        },
        (error: any) => {
          console.error("Failed to fetch base models:", error);
        }
      );
    }
  }, [apiKey, showOnlyAdvancedSettings, baseModel, setBaseModel]);

  // Fetch datasets for the intent group
  useEffect(() => {
    if (intentGroupId) {
      setLoadingDatasets(true);
      DatasetService.getDatasetsByIntentGroup(
        intentGroupId,
        0,
        100,
        setLoadingDatasets,
        apiKey
      ).subscribe(
        (response: any) => {
          if (response && response.items) {
            setDatasets(response.items);
          } else {
            setDatasets([]);
          }
        },
        (error: any) => {
          console.error("Failed to fetch datasets:", error);
          setDatasets([]);
          setLoadingDatasets(false);
        }
      );
    }
  }, [intentGroupId, apiKey]);

  // Helper to format dataset label
  const formatDatasetLabel = (dataset: FinetuneDataset) => {
    let sourceName = "";
    if (dataset.application_name && dataset.intent_name) {
      sourceName = `${dataset.application_name} / ${dataset.intent_name}`;
    } else if (dataset.application_name) {
      sourceName = dataset.application_name;
    } else if (dataset.intent_name) {
      sourceName = dataset.intent_name;
    } else if (dataset.intent_group_name) {
      sourceName = dataset.intent_group_name;
    } else {
      sourceName = "Unknown source";
    }

    return `${dataset.name} - ${sourceName}`;
  };

  // Handle dataset selection change
  const handleDatasetChange = (value: string) => {
    if (setSelectedSubDataset) {
      if (value) {
        const selectedDataset = datasets.find(
          (dataset) => dataset.id?.toString() === value
        );
        setSelectedSubDataset(selectedDataset || null);
      } else {
        setSelectedSubDataset(null);
      }
    }
  };

  return (
    <div className="space-y-6">
      {showOnlyAdvancedSettings && (
        <>
          {/* Only show Sub Dataset if intentGroupId is not null */}
          {intentGroupId && (
            <div className="space-y-3">
              <FieldLabel
                label="Sub Dataset"
                tooltip="Optional. If set, this will target training with a second finetune. Leave empty for standard finetuning."
              />
              <Select
                value={selectedSubDataset?.id?.toString() || ""}
                onValueChange={handleDatasetChange}
                disabled={isExporting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a sub dataset" />
                </SelectTrigger>
                <SelectContent>
                  {loadingDatasets ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2Icon className="w-4 h-4 animate-spin" />
                      <span className="ml-2 text-sm">Loading datasets...</span>
                    </div>
                  ) : datasets.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">No datasets found</div>
                  ) : (
                    datasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id?.toString() || ""}>
                        {formatDatasetLabel(dataset)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-3">
            <FieldLabel
              label="4-bit Quantization"
              tooltip="Use 4-bit quantization for finetuning"
            />
            <div className="flex items-center">
              <CustomSwitch
                checked={fourbit}
                onChange={(checked) => setFourbit(checked)}
                disabled={isExporting}
              />
            </div>
          </div>

          <div className="space-y-3">
            <FieldLabel
              label="Train on Responses Only"
              tooltip="Train on responses only"
            />
            <div className="flex items-center">
              <CustomSwitch
                checked={trainOnResponsesOnly}
                onChange={(checked) => setTrainOnResponsesOnly?.(checked)}
                disabled={isExporting}
              />
            </div>
          </div>

          <div className="space-y-3">
            <FieldLabel label="Prompt Deletions" />
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => setPromptDeletions([...promptDeletions, ""])}
                disabled={isExporting || !setPromptDeletions}
                className="gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Add Deletion
              </Button>

              {promptDeletions &&
                promptDeletions.map((deletion, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={deletion}
                      onChange={(e) => {
                        if (setPromptDeletions) {
                          const newDeletions = [...promptDeletions];
                          newDeletions[index] = e.target.value;
                          setPromptDeletions(newDeletions);
                        }
                      }}
                      disabled={isExporting || !setPromptDeletions}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (setPromptDeletions) {
                          setPromptDeletions(
                            promptDeletions.filter((_, i) => i !== index)
                          );
                        }
                      }}
                      disabled={isExporting || !setPromptDeletions}
                      className="gap-1"
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
            </div>
          </div>

          <Separator className="my-6" />

          <div className="space-y-6">
            <FieldLabel
              label="Training Parameters"
              tooltip="Configure model training parameters"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Warmup Ratio</Label>
                <Input
                  type="text"
                  value={warmupRatioText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setWarmupRatioText(value);
                    if (value === "") {
                      setWarmupRatio(0);
                    } else if (!isNaN(parseFloat(value))) {
                      setWarmupRatio(parseFloat(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="0.03"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Learning Rate</Label>
                <Input
                  type="text"
                  value={learningRateText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLearningRateText(value);
                    if (value === "") {
                      setLearningRate(0);
                    } else if (!isNaN(parseFloat(value))) {
                      setLearningRate(parseFloat(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="1e-4"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Per Device Train Batch Size</Label>
                <Input
                  type="text"
                  value={perDeviceTrainBatchSizeText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPerDeviceTrainBatchSizeText(value);
                    if (value === "") {
                      setPerDeviceTrainBatchSize?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setPerDeviceTrainBatchSize?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="8"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Gradient Accumulation Steps</Label>
                <Input
                  type="text"
                  value={gradientAccumulationStepsText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGradientAccumulationStepsText(value);
                    if (value === "") {
                      setGradientAccumulationSteps?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setGradientAccumulationSteps?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="4"
                />
              </div>
              
              {setRank && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Rank</Label>
                  <Input
                    type="text"
                    value={rankText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRankText(value);
                      if (value === "") {
                        setRank(0);
                      } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                        setRank(parseInt(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}
              
              {setAlpha && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Alpha</Label>
                  <Input
                    type="text"
                    value={alphaText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAlphaText(value);
                      if (value === "") {
                        setAlpha(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setAlpha(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}
              
              {setEpochs && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Epochs</Label>
                  <Input
                    type="text"
                    value={epochsText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEpochsText(value);
                      if (value === "") {
                        setEpochs(0);
                      } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                        setEpochs(parseInt(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="1"
                  />
                </div>
              )}
              
              {setWeightDecay && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Weight Decay</Label>
                  <Input
                    type="text"
                    value={weightDecayText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWeightDecayText(value);
                      if (value === "") {
                        setWeightDecay(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setWeightDecay(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0"
                  />
                </div>
              )}
              
              {setDropout && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Dropout</Label>
                  <Input
                    type="text"
                    value={dropoutText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDropoutText(value);
                      if (value === "") {
                        setDropout(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setDropout(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0.05"
                  />
                </div>
              )}
            </div>
            
            {setRslora && (
              <div className="space-y-3">
                <FieldLabel label="RSLoRA" />
                <div className="flex items-center">
                  <CustomSwitch
                    checked={rslora}
                    onChange={(checked) => setRslora(checked)}
                    disabled={isExporting}
                  />
                </div>
              </div>
            )}
          </div>

          {setTrainSplit && (
            <div className="space-y-3">
              <FieldLabel
                label="Train Split"
                tooltip="Percentage of data to use for training (remainder used for testing)"
              />
              <div className="space-y-2">
                <Slider
                  value={[trainSplit || 0.8]}
                  onValueChange={(value) => setTrainSplit(value[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={isExporting}
                  className="w-full"
                />
                <div className="text-sm text-muted-foreground text-center">
                  {((trainSplit || 0.8) * 100).toFixed(0)}% for training
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!showOnlyAdvancedSettings && (
        <>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <FieldLabel
                label="Prompt"
                tooltip="Optional prompt for the finetune"
              />
              <Button
                variant="outline"
                onClick={getPrompt}
                disabled={promptLoading || isExporting}
                className="gap-2"
              >
                {promptLoading ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <SparklesIcon className="w-4 h-4" />
                )}
                Suggest a Prompt
              </Button>
            </div>
            <textarea
              placeholder="Enter prompt (optional)"
              value={exportPrompt}
              onChange={(e) => setExportPrompt(e.target.value)}
              rows={4}
              disabled={isExporting}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <div className="space-y-3">
            <FieldLabel label="Base Model" required />
            <Select
              value={baseModel}
              onValueChange={setBaseModel}
              disabled={isExporting}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select base model" />
              </SelectTrigger>
              <SelectContent>
                {baseModels.map((model) => (
                  <SelectItem key={model} value={model}>
                    {model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {!hideAdvancedSettings && !showOnlyAdvancedSettings && (
        <>
          {/* Only show Sub Dataset if intentGroupId is not null */}
          {intentGroupId && (
            <div className="space-y-3">
              <FieldLabel
                label="Sub Dataset"
                tooltip="Optional. If set, this will target training with a second finetune. Leave empty for standard finetuning."
              />
              <Select
                value={selectedSubDataset?.id?.toString() || ""}
                onValueChange={handleDatasetChange}
                disabled={isExporting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a sub dataset" />
                </SelectTrigger>
                <SelectContent>
                  {loadingDatasets ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2Icon className="w-4 h-4 animate-spin" />
                      <span className="ml-2 text-sm">Loading datasets...</span>
                    </div>
                  ) : datasets.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">No datasets found</div>
                  ) : (
                    datasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id?.toString() || ""}>
                        {formatDatasetLabel(dataset)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-3">
            <FieldLabel
              label="4-bit Quantization"
              tooltip="Use 4-bit quantization for finetuning"
            />
            <div className="flex items-center">
              <CustomSwitch
                checked={fourbit}
                onChange={(checked) => setFourbit(checked)}
                disabled={isExporting}
              />
            </div>
          </div>

          <div className="space-y-3">
            <FieldLabel label="Prompt Deletions" />
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => setPromptDeletions([...promptDeletions, ""])}
                disabled={isExporting || !setPromptDeletions}
                className="gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Add Deletion
              </Button>

              {promptDeletions &&
                promptDeletions.map((deletion, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={deletion}
                      onChange={(e) => {
                        if (setPromptDeletions) {
                          const newDeletions = [...promptDeletions];
                          newDeletions[index] = e.target.value;
                          setPromptDeletions(newDeletions);
                        }
                      }}
                      disabled={isExporting || !setPromptDeletions}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (setPromptDeletions) {
                          setPromptDeletions(
                            promptDeletions.filter((_, i) => i !== index)
                          );
                        }
                      }}
                      disabled={isExporting || !setPromptDeletions}
                      className="gap-1"
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
            </div>
          </div>

          {setTrainSplit && (
            <div className="space-y-3">
              <FieldLabel
                label="Train Split"
                tooltip="Percentage of data to use for training (remainder used for testing)"
              />
              <div className="space-y-2">
                <Slider
                  value={[trainSplit || 0.8]}
                  onValueChange={(value) => setTrainSplit(value[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={isExporting}
                  className="w-full"
                />
                <div className="text-sm text-muted-foreground text-center">
                  {((trainSplit || 0.8) * 100).toFixed(0)}% for training
                </div>
              </div>
            </div>
          )}

          <Separator className="my-6" />

          <div className="space-y-6">
            <FieldLabel
              label="Training Parameters"
              tooltip="Configure model training parameters"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Warmup Steps</Label>
                <Input
                  type="text"
                  value={warmupStepsText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setWarmupStepsText(value);
                    if (value === "") {
                      setWarmupSteps(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setWarmupSteps(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="5"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Learning Rate</Label>
                <Input
                  type="text"
                  value={learningRateText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLearningRateText(value);
                    if (value === "") {
                      setLearningRate(0);
                    } else if (!isNaN(parseFloat(value))) {
                      setLearningRate(parseFloat(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="1e-4"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Per Device Train Batch Size</Label>
                <Input
                  type="text"
                  value={perDeviceTrainBatchSizeText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPerDeviceTrainBatchSizeText(value);
                    if (value === "") {
                      setPerDeviceTrainBatchSize?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setPerDeviceTrainBatchSize?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="8"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Gradient Accumulation Steps</Label>
                <Input
                  type="text"
                  value={gradientAccumulationStepsText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGradientAccumulationStepsText(value);
                    if (value === "") {
                      setGradientAccumulationSteps?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setGradientAccumulationSteps?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="4"
                />
              </div>
              
              {setAlpha && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Alpha</Label>
                  <Input
                    type="text"
                    value={alphaText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAlphaText(value);
                      if (value === "") {
                        setAlpha(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setAlpha(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}
              
              {setRank && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Rank</Label>
                  <Input
                    type="text"
                    value={rankText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRankText(value);
                      if (value === "") {
                        setRank(0);
                      } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                        setRank(parseInt(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}
              
              {setEpochs && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Epochs</Label>
                  <Input
                    type="text"
                    value={epochsText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEpochsText(value);
                      if (value === "") {
                        setEpochs(0);
                      } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                        setEpochs(parseInt(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="1"
                  />
                </div>
              )}
              
              {setWeightDecay && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Weight Decay</Label>
                  <Input
                    type="text"
                    value={weightDecayText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWeightDecayText(value);
                      if (value === "") {
                        setWeightDecay(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setWeightDecay(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0"
                  />
                </div>
              )}
              
              {setDropout && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Dropout</Label>
                  <Input
                    type="text"
                    value={dropoutText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDropoutText(value);
                      if (value === "") {
                        setDropout(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setDropout(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0.05"
                  />
                </div>
              )}
            </div>
            
            {setRslora && (
              <div className="space-y-3">
                <FieldLabel label="RSLoRA" />
                <div className="flex items-center">
                  <CustomSwitch
                    checked={rslora}
                    onChange={(checked) => setRslora(checked)}
                    disabled={isExporting}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FinetuneSettings;


